import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { Navbar } from '../components/navbar';
import { Sidebar } from '../components/sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { getQueryFn } from '../lib/queryClient';
import { useAuth } from '../hooks/use-auth';

interface Company {
  id: number;
  name: string;
  industry?: string;
}

export default function CompaniesDashboard() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');

  const { data: companies, isLoading } = useQuery<Company[]>({
    queryKey: ['/api/users/me/companies'],
    queryFn: getQueryFn(),
    // Ensure we see newly created companies immediately after navigation
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

  const filtered = useMemo(() => {
    const list = companies || [];
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter((c) =>
      c.name.toLowerCase().includes(q) || (c.industry || '').toLowerCase().includes(q)
    );
  }, [companies, search]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 p-8">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Companies</h1>
                <p className="text-gray-600 mt-1">Manage your organization profiles and quickly jump to actions.</p>
              </div>
              <div className="flex items-center gap-3 w-full md:w-auto">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search companies or industry..."
                  className="flex-1 md:w-80 border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Link href="/company-profile">
                  <Button>New Company</Button>
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <Card>
                <CardContent className="p-4">
                  <div className="text-sm text-gray-500">Total Companies</div>
                  <div className="text-2xl font-semibold">{companies?.length ?? 0}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-sm text-gray-500">Showing</div>
                  <div className="text-2xl font-semibold">{filtered.length}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-sm text-gray-500">Actions</div>
                  <div className="mt-1">
                    {/* Only show Create Company button for system users */}
                    {user?.is_system_user && (
                      <Link href="/company-profile">
                        <Button variant="outline" size="sm">Create Company</Button>
                      </Link>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Registered Companies</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Industry</TableHead>
                        <TableHead className="w-48">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Array.from({ length: 6 }).map((_, i) => (
                        <TableRow key={i} className="animate-pulse">
                          <TableCell>
                            <div className="h-4 w-40 bg-gray-200 rounded" />
                          </TableCell>
                          <TableCell>
                            <div className="h-4 w-32 bg-gray-200 rounded" />
                          </TableCell>
                          <TableCell>
                            <div className="h-8 w-28 bg-gray-200 rounded" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : filtered && filtered.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Industry</TableHead>
                        <TableHead className="w-48">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((company) => (
                        <TableRow key={company.id}>
                          <TableCell className="font-medium">{company.name}</TableCell>
                          <TableCell className="text-gray-600">{company.industry || '—'}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Link href={`/company-profile/${company.id}`}>
                                <Button variant="outline" size="sm">
                                  {user?.is_system_user ? "View / Edit" : "View Details"}
                                </Button>
                              </Link>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="py-12 text-center">
                    <div className="text-5xl mb-4">🏢</div>
                    <h3 className="text-lg font-medium text-gray-900">No companies found</h3>
                    <p className="text-gray-600 mt-1">Create your first company to get started with emissions tracking.</p>
                    <div className="mt-6">
                      <Link href="/company-profile">
                        <Button>Create New Company</Button>
                      </Link>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
}
